import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Button } from './ui';

const GuaranteePopup = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const popupShown = sessionStorage.getItem('guaranteePopupShown');

    if (!popupShown) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('guaranteePopupShown', 'true');
  };

  const handleCTAClick = () => {
    handleClose();
  };

  const benefits = t('home.guaranteePopup.benefits', { returnObjects: true });

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-bg/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className="relative w-full max-w-md overflow-hidden rounded-card border border-white/[.08] bg-surface p-6 md:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={handleClose}
                className="absolute right-4 top-4 text-muted transition-colors hover:text-body"
                aria-label={t('home.guaranteePopup.close')}
              >
                <X className="h-4 w-4" />
              </button>

              <div className="pr-6">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted">
                  Alturion Lab.
                </p>
                <h2 className="text-xl font-semibold tracking-tight text-body md:text-2xl">
                  {t('home.guaranteePopup.guarantee')}
                </h2>
                <p className="mt-2 text-sm text-muted">{t('home.guaranteePopup.subtitle')}</p>
              </div>

              <div className="mt-6 rounded-card border border-white/[.08] bg-bg/50 px-4 py-3">
                <p className="text-sm font-medium text-body">
                  {t('home.guaranteePopup.limitedTime')}
                </p>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-muted">
                {t('home.guaranteePopup.description')}
              </p>

              {Array.isArray(benefits) && (
                <ul className="mt-5 space-y-2.5">
                  {benefits.map((benefit, index) => {
                    const text = benefit.replace(/^[^\wÀ-ÿ]+/, '').trim();
                    return (
                      <li key={index} className="flex items-start gap-2.5 text-sm text-muted">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                        <span>{text || benefit}</span>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="mt-7 flex flex-col gap-3">
                <Button to="/indicators" onClick={handleCTAClick} className="w-full">
                  {t('home.guaranteePopup.cta')}
                </Button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-sm text-muted transition-colors hover:text-body"
                >
                  {t('home.guaranteePopup.close')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GuaranteePopup;
